import React, { Fragment, useCallback, useMemo, useEffect } from 'react'
import { assoc, isEmpty } from 'ramda'

import PlainInput from 'app/common/ui-next/plain-input'
import PlainLabel from 'app/common/ui-next/plain-label'
import { PlainItemsSelector } from 'app/common/ui-next/plain-selector'
import PlainHelperText from 'app/common/ui-next/plain-helper-text'
import {
  defaultVendingMachineKind,
  VendingMachineKind,
  vendingMachineKindItems,
} from 'app/dataTypes/vending/MachineKind.types'

import {
  useMachineForm,
  useMachineValidate,
  useAvailableOrgs,
  useVendingMachineSingle,
} from 'app/dataProviders/VendingDataProvider'

import { useActualModels } from './useActualModels'
import { useActualVersions } from './useActualVersions'
import { useActualBuildings } from './useActualBuildings'
import { useActualSalePoints } from './useActualSalePoints'

const emptySelection = []

type Props = {
  disabled: boolean
}

const EditMachine = ({ disabled }: Props): JSX.Element => {
  const [{ title, number, orgId, buildingId, kind }, { fillFromData, update }] =
    useMachineForm()
  const [{ id: sourceMachineId }] = useVendingMachineSingle()
  const [errors, , resetErrors] = useMachineValidate()

  const { buildings, loadingBuildings, buildingSelection, changeCurrentBuilding } =
    useActualBuildings()

  const { salePoints, loadingSalePoints, salePointSelection, changeCurrentSalePoint } =
    useActualSalePoints()

  useEffect(() => {
    fillFromData()
  }, [sourceMachineId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      resetErrors()
    }
  }, [resetErrors])

  const [availableOrgs, loadingOrgs, requestOrgs] = useAvailableOrgs()
  useEffect(() => {
    requestOrgs()
  }, [requestOrgs])

  const orgSelection = useMemo(() => (orgId ? [String(orgId)] : []), [orgId])

  const changeCurrentOrg = useCallback(
    ([orgId_]: Array<string>) => {
      resetErrors()
      const nextOrgId = parseInt(orgId_, 10) || 0
      update((state) => ({
        ...state,
        orgId: nextOrgId,
        buildingId: 0,
        salePointId: 0,
      }))
    },
    [update, resetErrors],
  )

  const { models, loadingModels, modelSelection, changeCurrentModel } = useActualModels()

  const modelNotYetSelected = isEmpty(modelSelection)

  const { versions, loadingVersions, versionSelection, changeCurrentVersion } =
    useActualVersions(modelSelection)

  const kindSelection = useMemo(
    () => [kind || defaultVendingMachineKind],
    [kind],
  )

  const changeCurrentKind = useCallback(
    ([kind_]: Array<string>) => {
      resetErrors()
      update(
        assoc('kind', (kind_ as VendingMachineKind) || defaultVendingMachineKind),
      )
    },
    [update, resetErrors],
  )

  const changeInputField = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value, id } = e.currentTarget
      if (id === 'title' || id === 'number') {
        resetErrors()
        update(assoc(id, value))
      }
    },
    [update, resetErrors],
  )

  return (
    <Fragment>
      <PlainItemsSelector
        label="Производитель"
        header="Выберите производителя"
        pleaseSelectText="Выберите производителя"
        items={models}
        selection={modelSelection}
        loading={loadingModels}
        defaultSelection={emptySelection}
        onChange={changeCurrentModel}
        selectedAllOnEmptySelection={false}
        disabled={disabled}
      />
      <PlainHelperText error>{errors.modelId}</PlainHelperText>

      <PlainItemsSelector
        label="Модель аппарата"
        header="Выберите модель аппарата"
        pleaseSelectText="Выберите модель аппарата"
        items={versions}
        selection={versionSelection}
        loading={loadingVersions}
        defaultSelection={emptySelection}
        onChange={changeCurrentVersion}
        selectedAllOnEmptySelection={false}
        disabled={disabled || modelNotYetSelected}
      />
      <PlainHelperText error>{errors.versionId}</PlainHelperText>

      <PlainItemsSelector
        label="Вид аппарата"
        header="Выберите вид аппарата"
        pleaseSelectText="Выберите вид аппарата"
        items={vendingMachineKindItems}
        selection={kindSelection}
        onChange={changeCurrentKind}
        selectedAllOnEmptySelection={false}
        disabled={disabled}
      />

      <PlainLabel htmlFor="title" displayAsBlock>
        {'Наименование аппарата'}
      </PlainLabel>
      <PlainInput
        id="title"
        value={title}
        onChange={changeInputField}
        disabled={disabled}
        error={!!errors.title}
        displayAsBlock
      />
      <PlainHelperText error>{errors.title}</PlainHelperText>

      <PlainLabel htmlFor="number" displayAsBlock>
        {'Номер аппарата'}
      </PlainLabel>
      <PlainInput
        id="number"
        value={number}
        onChange={changeInputField}
        error={!!errors.number}
        displayAsBlock
      />
      <PlainHelperText error>{errors.number}</PlainHelperText>

      <PlainItemsSelector
        label="Место размещения"
        header="Выберите заведение, где стоит аппарат"
        pleaseSelectText="Выберите заведение"
        items={availableOrgs}
        selection={orgSelection}
        loading={loadingOrgs}
        defaultSelection={emptySelection}
        onChange={changeCurrentOrg}
        selectedAllOnEmptySelection={false}
        disabled={disabled}
      />
      <PlainHelperText error>{errors.orgId}</PlainHelperText>

      <PlainItemsSelector
        label="Корпус"
        header="Выберите корпус, где стоит аппарат"
        pleaseSelectText="Выберите корпус"
        items={buildings}
        selection={buildingSelection}
        loading={loadingBuildings}
        defaultSelection={emptySelection}
        onChange={changeCurrentBuilding}
        selectedAllOnEmptySelection={false}
        disabled={disabled || !orgId || loadingBuildings}
      />
      <PlainHelperText error>{errors.buildingId}</PlainHelperText>

      <PlainItemsSelector
        label="Точка продаж"
        header="Выберите точку продаж, где стоит аппарат"
        pleaseSelectText="Выберите точку продаж"
        items={salePoints}
        selection={salePointSelection}
        loading={loadingSalePoints}
        defaultSelection={emptySelection}
        onChange={changeCurrentSalePoint}
        selectedAllOnEmptySelection={false}
        disabled={disabled || !orgId || !buildingId || loadingSalePoints}
        emptyTitle="Отсутствует"
      />
      <PlainHelperText error>{errors.salePointId}</PlainHelperText>
    </Fragment>
  )
}

export default EditMachine
